// Studby — public landing page with the "Free to try" CTA.
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sprout, Camera, FileText, MessageCircle, ArrowRight, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const FEATURES = [
  { icon: Camera, title: "Photo Solver", desc: "Snap any problem — any subject — and get a clear, step-by-step solution, plus an ‘explain like I'm 5’." },
  { icon: FileText, title: "Notes → Quiz", desc: "Snap a photo or upload a PDF of your notes and Studby builds a 10-question quiz, then grades you instantly." },
  { icon: MessageCircle, title: "AI Chat Tutor", desc: "A patient tutor that remembers your chat and guides you instead of just handing over answers." },
];

export default function Landing() {
  const { user } = useAuth();
  const startHref = user ? "/app/dashboard" : "/signup";

  return (
    <div className="min-h-screen bg-background">
      {/* Nav */}
      <header className="border-b border-border bg-card/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="grid place-items-center w-9 h-9 rounded-lg bg-primary text-primary-foreground">
              <Sprout size={20} aria-hidden="true" />
            </span>
            <span className="font-heading font-extrabold text-xl text-primary">Studby</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" size="sm" data-testid="nav-login-button">Log in</Button>
            </Link>
            <Link to={startHref}>
              <Button size="sm" data-testid="nav-signup-button" className="rounded-full">Free to try</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-3xl mx-auto px-5 pt-16 pb-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <span className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-accent font-semibold mb-4">
            <Sparkles size={14} aria-hidden="true" /> Your AI study buddy
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-foreground">
            Study smarter,<br />not harder.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl">
            Solve homework from a photo, turn notes into quizzes, and chat with a tutor
            that actually explains things. Built for your phone.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link to={startHref}>
              <Button size="lg" data-testid="hero-cta-button" className="rounded-full text-base px-7">
                Free to try <ArrowRight size={18} className="ml-1" aria-hidden="true" />
              </Button>
            </Link>
          </div>
          <p className="mt-4 text-sm text-muted-foreground max-w-xl">
            Free 3-day trial. Then{" "}
            <span className="font-semibold text-foreground">€3.99/mo</span> for 50 questions,
            or <span className="font-semibold text-foreground">€12.99/mo</span> for 250 questions.
          </p>
        </motion.div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-5 py-14">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-8">
          Everything you need to ace it
        </h2>
        <div className="grid sm:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="bg-card border border-border rounded-2xl p-6 hover:shadow-md transition-shadow"
                data-testid={`feature-card-${i}`}
              >
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-secondary text-primary mb-4">
                  <Icon size={22} aria-hidden="true" />
                </span>
                <h3 className="font-heading text-lg font-bold mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-3xl mx-auto px-5 py-16 text-center">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
          Start studying smarter today
        </h2>
        <p className="mt-3 text-muted-foreground max-w-xl mx-auto">
          Try it free for 3 days — 5 questions a day. Then just €3.99/month for
          50 questions, or €12.99/month for 250 questions (Pro). Cancel anytime.
        </p>
        <Link to={startHref}>
          <Button size="lg" className="mt-7 rounded-full text-base px-8" data-testid="pricing-cta-button">
            Free to try
          </Button>
        </Link>
      </section>

      <footer className="border-t border-border py-8 text-center text-sm text-muted-foreground">
        © {new Date().getFullYear()} Studby · Study smarter.
      </footer>
    </div>
  );
}
